import {
  Form as FormStructure,
  Field as FormField,
  FormType,
  Section,
  SelectField,
} from "@/models/new/forms.types"
import * as React from "react"
import * as Dialog from "@radix-ui/react-dialog"

interface ListItem {
  id: string
  value: string
  [key: string]: unknown
}

interface FormStructureVizProps {
  forms: FormStructure[]
  listItemsMap: Record<string, ListItem[]>
}

const formTypeLabels: Record<FormType, string> = {
  incident: "Incidents",
  inspection: "Inspections",
  issue: "Issues",
}

const getFormScopeLabel = (organizationId: string) =>
  organizationId === "global" ? "Global template" : "Organization form"

const FormStructureViz = ({ forms, listItemsMap }: FormStructureVizProps) => {
  const [selectedFormId, setSelectedFormId] = React.useState(forms[0]?.id ?? "")
  const [selectedOptions, setSelectedOptions] = React.useState<ListItem[] | null>(null)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [selectedFieldLabel, setSelectedFieldLabel] = React.useState("")

  const groupedForms = React.useMemo(
    () =>
      forms.reduce(
        (groups, form) => {
          groups[form.type].push(form)
          return groups
        },
        {
          incident: [] as FormStructure[],
          inspection: [] as FormStructure[],
          issue: [] as FormStructure[],
        },
      ),
    [forms],
  )

  const selectedForm = forms.find((form) => form.id === selectedFormId) ?? forms[0] ?? null

  // Render a section
  const renderSection = (section: Section) => {
    return (
      <div key={section.id} className="p-4 rounded-lg border bg-white border-gray-200">
        <h3 className="text-lg font-semibold mb-3 flex items-center">
          {section.label}
          {section.visibility === "desktop" && (
            <span className="ml-2 px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
              desktop only
            </span>
          )}
          {section.visibility === "mobile" && (
            <span className="ml-2 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
              mobile only
            </span>
          )}
        </h3>
        {section.conditionalDisplay && (
          <div className="text-xs text-gray-500 mb-2">
            Conditional: shown when field "{section.conditionalDisplay.triggerField}"{" "}
            {section.conditionalDisplay.conditions.map((c, i) => (
              <span key={i}>
                {c.operator} {Array.isArray(c.value) ? c.value.join(", ") : c.value}
                {i < section.conditionalDisplay!.conditions.length - 1 ? " AND " : ""}
              </span>
            ))}
          </div>
        )}
        <div className="space-y-3">{section.fields.map((field) => renderField(field))}</div>
      </div>
    )
  }

  // Render a single field
  const renderField = (field: FormField) => {
    // Handle dynamic groups (which contain nested fields)
    if (field.type === "dynamicGroup") {
      return (
        <div key={field.id} className="p-3 border rounded-md mb-2 bg-gray-50">
          <div className="font-medium mb-2">{field.label}</div>
          <div className="ml-4 space-y-2">
            {field.fields.map((subField) => renderField(subField))}
          </div>
        </div>
      )
    }

    // Check if this is a select field with options
    const isSelectField = field.type === "select"
    const selectField = isSelectField ? (field as SelectField) : null

    // Render regular fields
    return (
      <div
        key={field.id}
        className={`p-3 border rounded-md mb-2 bg-white ${isSelectField ? "cursor-pointer hover:bg-gray-50" : ""}`}
        onClick={() => {
          if (selectField?.options) {
            setSelectedOptions(listItemsMap[selectField.options.list] || [])
            setSelectedFieldLabel(field.label)
            setIsModalOpen(true)
          }
        }}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="font-medium">{field.label}</div>
            <div className="text-sm text-gray-500">{field.name && `field name: ${field.name}`}</div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
              {field.type}
              {selectField && ` (${selectField.selectionType})`}
            </span>
            {"required" in field && field.required && (
              <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                required
              </span>
            )}
            {field.visibility === "desktop" && (
              <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                desktop only
              </span>
            )}
            {field.visibility === "mobile" && (
              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                mobile only
              </span>
            )}
          </div>
        </div>

        {"description" in field && field.description && (
          <div className="text-sm text-gray-600 mt-1">{field.description}</div>
        )}

        {field.conditionalDisplay && (
          <div className="text-xs text-gray-500 mt-2 p-2 bg-yellow-50 rounded">
            <span className="font-medium">Conditional:</span> shown when field "
            {field.conditionalDisplay.triggerField}"{" "}
            {field.conditionalDisplay.conditions.map((c, i) => (
              <span key={i}>
                {c.operator} {Array.isArray(c.value) ? c.value.join(", ") : c.value}
                {i < field.conditionalDisplay!.conditions.length - 1 ? " AND " : ""}
              </span>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="w-full max-w-6xl mx-auto p-6 bg-gray-50">
        {forms.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
            No form structures are available.
          </div>
        )}

        {/* Form Selector */}
        {forms.length > 0 && (
          <div className="mb-6">
            <select
              className="w-full max-w-md p-2 border rounded-md"
              value={selectedForm?.id ?? ""}
              onChange={(e) => setSelectedFormId(e.target.value)}
            >
              {Object.entries(groupedForms).map(([formType, groupedTypeForms]) => {
                if (groupedTypeForms.length === 0) return null

                return (
                  <optgroup
                    key={formType}
                    label={`${formTypeLabels[formType as FormType]} (${groupedTypeForms.length})`}
                  >
                    {groupedTypeForms.map((form) => (
                      <option key={form.id} value={form.id}>
                        {form.title}
                        {form.incidentType ? ` (${form.incidentType})` : ""}
                        {` - ${getFormScopeLabel(form.organizationId)}`}
                      </option>
                    ))}
                  </optgroup>
                )
              })}
            </select>
          </div>
        )}

        {selectedForm && (
          <div>
            {/* Form Header */}
            <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
              <div className="flex items-center space-x-4">
                {selectedForm.icon && (
                  <img src={selectedForm.icon} alt="" className="w-12 h-12 object-contain" />
                )}
                <div>
                  <h1 className="text-2xl font-bold">{selectedForm.title}</h1>
                  <div className="text-gray-500">
                    Type: {formTypeLabels[selectedForm.type]}
                    {selectedForm.incidentType && ` | Incident Type: ${selectedForm.incidentType}`}
                    {` | Scope: ${getFormScopeLabel(selectedForm.organizationId)}`}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-6">
              {selectedForm.fields.map((section) => renderSection(section))}
            </div>
          </div>
        )}
      </div>

      <Dialog.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[90vw] max-w-md max-h-[85vh] overflow-y-auto">
            <Dialog.Title className="text-lg font-semibold mb-4">
              {selectedFieldLabel} - Available Options
            </Dialog.Title>

            <div className="space-y-2">
              {selectedOptions?.map((item) => (
                <div key={item.id} className="p-2 border rounded hover:bg-gray-50">
                  {item.value}
                </div>
              ))}
            </div>

            <Dialog.Close asChild>
              <button
                className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md"
                onClick={() => setIsModalOpen(false)}
              >
                Close
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}

export default FormStructureViz
